import { useState, useEffect } from "react";
import { MessageSquare, Send, Shield, Trophy, Mail, User } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { getTeams, sendMessage, getSettings } from "../lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [teams, setTeams] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    sender_name: "",
    sender_email: "",
    recipient_type: "admin",
    team_id: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    Promise.all([getTeams(), getSettings()])
      .then(([teamsRes, settingsRes]) => {
        setTeams(teamsRes.data);
        setSettings(settingsRes.data);
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.sender_name || !formData.sender_email || !formData.subject || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.recipient_type === "team_captain" && !formData.team_id) {
      toast.error("Please select a team");
      return;
    }

    setLoading(true);
    try {
      await sendMessage(formData);
      toast.success("Message sent successfully!");
      setFormData({
        sender_name: "",
        sender_email: "",
        recipient_type: "admin",
        team_id: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  const tournamentLogo = settings?.logo;

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            {tournamentLogo ? (
              <img
                src={tournamentLogo}
                alt="Tournament Logo"
                className="w-20 h-20 rounded-2xl object-cover border-2 border-primary/30"
              />
            ) : (
              <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center border-2 border-primary/30">
                <Trophy className="w-10 h-10 text-primary" />
              </div>
            )}
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight">
            GOODWILLERS
          </h1>
          <p className="text-muted-foreground font-heading uppercase tracking-wider">
            Indoor Tournament
          </p>
        </div>

        {/* Contact Form */}
        <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <MessageSquare className="w-6 h-6 text-primary" />
            <h2 className="font-heading text-xl font-bold uppercase tracking-wider">
              Contact Us
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sender Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Your Name *
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={formData.sender_name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, sender_name: e.target.value }))
                    }
                    placeholder="John Doe"
                    className="pl-10 bg-background border-border"
                    data-testid="sender-name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Your Email *
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="email"
                    value={formData.sender_email}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, sender_email: e.target.value }))
                    }
                    placeholder="john@example.com"
                    className="pl-10 bg-background border-border"
                    data-testid="sender-email-input"
                  />
                </div>
              </div>
            </div>

            {/* Recipient */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Send To *
                </Label>
                <Select
                  value={formData.recipient_type}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, recipient_type: value, team_id: "" }))
                  }
                >
                  <SelectTrigger
                    className="bg-background border-border"
                    data-testid="recipient-type-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="admin">League Admin</SelectItem>
                    <SelectItem value="team_captain">Team Captain</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.recipient_type === "team_captain" && (
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Select Team *
                  </Label>
                  <Select
                    value={formData.team_id}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, team_id: value }))
                    }
                  >
                    <SelectTrigger
                      className="bg-background border-border"
                      data-testid="team-select"
                    >
                      <SelectValue placeholder="Choose a team" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {teams.map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Subject *
              </Label>
              <Input
                value={formData.subject}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, subject: e.target.value }))
                }
                placeholder="What is this about?"
                className="bg-background border-border"
                data-testid="subject-input"
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Message *
              </Label>
              <Textarea
                value={formData.message}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, message: e.target.value }))
                }
                placeholder="Write your message here..."
                className="bg-background border-border min-h-[150px]"
                data-testid="message-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider btn-glow"
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {loading ? "Sending..." : "Send Message"}
            </Button>
          </form>
        </div>

        {/* Back Link */}
        <div className="text-center mt-6">
          <a
            href="/"
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            ← Back to Tournament
          </a>
        </div>
      </div>
    </div>
  );
}
